import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme
} from '@mui/material';
import {
  Psychology,
  SentimentSatisfied,
  SentimentDissatisfied,
  SentimentNeutral,
  TrendingUp,
  TrendingDown
} from '@mui/icons-material';
import { advancedReportsAPI } from '../../services/api';
import { ExportButton } from './ExportButton';

interface SentimentAnalysisProps {
  dateRange: {
    startDate: Date;
    endDate: Date;
  };
  selectedAgent: number | 'all';
  selectedChannel: number | 'all';
}

interface SentimentData {
  overall_sentiment_score: number;
  sentiment_distribution: {
    positive: number;
    negative: number;
    neutral: number;
  };
  sentiment_trend: Array<{
    date: string;
    sentiment_score: number;
    message_count: number;
  }>;
  top_positive_messages: Array<{
    content: string;
    score: number;
    date: string;
    conversation_id: number;
  }>;
  top_negative_messages: Array<{
    content: string;
    score: number;
    date: string;
    conversation_id: number;
  }>;
  sentiment_by_channel: Record<string, { avg_sentiment: number; message_count: number }>;
  sentiment_by_agent: Record<string, { avg_sentiment: number; message_count: number }>;
  keyword_analysis: {
    positive_keywords: string[];
    negative_keywords: string[];
  };
}

const SentimentMeter: React.FC<{ score: number; size?: 'small' | 'medium' | 'large' }> = ({ 
  score, 
  size = 'medium' 
}) => {
  const theme = useTheme();
  
  const getSentimentColor = (score: number) => {
    if (score > 0.1) return theme.palette.success.main;
    if (score < -0.1) return theme.palette.error.main;
    return theme.palette.warning.main;
  };
  
  const getSentimentLabel = (score: number) => {
    if (score > 0.1) return 'Positive';
    if (score < -0.1) return 'Negative';
    return 'Neutral';
  };
  
  const getSentimentIcon = (score: number) => {
    if (score > 0.1) return <SentimentSatisfied />;
    if (score < -0.1) return <SentimentDissatisfied />;
    return <SentimentNeutral />;
  };
  
  // Convert score from [-1, 1] to [0, 100] for progress bar
  const progressValue = ((score + 1) / 2) * 100;
  
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {getSentimentIcon(score)}
      <Box sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant={size === 'small' ? 'caption' : 'body2'}>
            {getSentimentLabel(score)}
          </Typography>
          <Typography variant={size === 'small' ? 'caption' : 'body2'} color="text.secondary">
            {score.toFixed(3)}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progressValue}
          sx={{
            height: size === 'small' ? 4 : 6,
            backgroundColor: 'grey.200',
            '& .MuiLinearProgress-bar': {
              backgroundColor: getSentimentColor(score)
            }
          }}
        />
      </Box>
    </Box>
  );
};

export const SentimentAnalysis: React.FC<SentimentAnalysisProps> = ({
  dateRange,
  selectedAgent,
  selectedChannel
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sentimentData, setSentimentData] = useState<SentimentData | null>(null);

  useEffect(() => {
    fetchSentimentData();
  }, [dateRange, selectedAgent, selectedChannel]);

  const fetchSentimentData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {
        start_date: dateRange.startDate.toISOString().split('T')[0],
        end_date: dateRange.endDate.toISOString().split('T')[0]
      };

      if (selectedAgent !== 'all') {
        params.agent_id = selectedAgent;
      }
      if (selectedChannel !== 'all') {
        params.channel_id = selectedChannel;
      }

      const data = await advancedReportsAPI.getSentimentAnalysis(params);
      setSentimentData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch sentiment analysis');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!sentimentData) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No sentiment data available for the selected period.
      </Alert>
    );
  }

  const totalMessages = sentimentData.sentiment_distribution.positive + 
                       sentimentData.sentiment_distribution.negative + 
                       sentimentData.sentiment_distribution.neutral;

  // Prepare data for export
  const exportData = [
    {
      overall_sentiment_score: sentimentData?.overall_sentiment_score,
      positive_count: sentimentData?.sentiment_distribution.positive,
      neutral_count: sentimentData?.sentiment_distribution.neutral,
      negative_count: sentimentData?.sentiment_distribution.negative
    },
    ...(Array.isArray(sentimentData?.sentiment_trend) ? sentimentData.sentiment_trend : []),
    ...(Array.isArray(sentimentData?.top_positive_messages) ? sentimentData.top_positive_messages.map(m => ({ ...m, sentiment: 'positive' })) : []),
    ...(Array.isArray(sentimentData?.top_negative_messages) ? sentimentData.top_negative_messages.map(m => ({ ...m, sentiment: 'negative' })) : [])
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Psychology sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h5">
            Sentiment Analysis
          </Typography>
        </Box>
        <ExportButton
          data={exportData}
          filename="sentiment_analysis"
          metadata={{ dateRange, filters: { agent: selectedAgent, channel: selectedChannel } }}
        />
      </Box>

      {/* Overall Sentiment Score */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Overall Sentiment Score
              </Typography>
              <SentimentMeter score={sentimentData.overall_sentiment_score} size="large" />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Based on analysis of {totalMessages.toLocaleString()} messages
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Sentiment Distribution
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SentimentSatisfied color="success" />
                    <Typography>Positive</Typography>
                  </Box>
                  <Typography variant="h6" color="success.main">
                    {sentimentData.sentiment_distribution.positive}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SentimentNeutral color="warning" />
                    <Typography>Neutral</Typography>
                  </Box>
                  <Typography variant="h6" color="warning.main">
                    {sentimentData.sentiment_distribution.neutral}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SentimentDissatisfied color="error" />
                    <Typography>Negative</Typography>
                  </Box>
                  <Typography variant="h6" color="error.main">
                    {sentimentData.sentiment_distribution.negative}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Sentiment by Channel/Agent */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Sentiment by Channel
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {Object.entries(sentimentData.sentiment_by_channel).map(([channel, data]) => (
                  <Box key={channel}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {channel}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {data.message_count} messages
                      </Typography>
                    </Box>
                    <SentimentMeter score={data.avg_sentiment} size="small" />
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Sentiment by Agent
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {Object.entries(sentimentData.sentiment_by_agent).slice(0, 5).map(([agent, data]) => (
                  <Box key={agent}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">
                        {agent}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {data.message_count} messages
                      </Typography>
                    </Box>
                    <SentimentMeter score={data.avg_sentiment} size="small" />
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Keywords Analysis */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="success.main">
                Most Positive Keywords
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {sentimentData.keyword_analysis.positive_keywords.map((keyword, index) => (
                  <Chip
                    key={index}
                    label={keyword}
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="error.main">
                Most Negative Keywords
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {sentimentData.keyword_analysis.negative_keywords.map((keyword, index) => (
                  <Chip
                    key={index}
                    label={keyword}
                    size="small"
                    color="error"
                    variant="outlined"
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Sample Messages */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="success.main">
                Most Positive Messages
              </Typography>
              <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                {sentimentData.top_positive_messages.map((message, index) => (
                  <Box key={index} sx={{ mb: 2, p: 1, bgcolor: 'success.light', borderRadius: 1, opacity: 0.1 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      "{message.content}..."
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">
                        Score: {message.score.toFixed(3)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(message.date).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="error.main">
                Most Negative Messages
              </Typography>
              <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                {sentimentData.top_negative_messages.map((message, index) => (
                  <Box key={index} sx={{ mb: 2, p: 1, bgcolor: 'error.light', borderRadius: 1, opacity: 0.1 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      "{message.content}..."
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">
                        Score: {message.score.toFixed(3)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(message.date).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};